/**
 * Dynamic Internal Search Index — maps user tokens to real routes across the
 * entire site. Consumed by the header search + ⌘K modal + /search page.
 *
 * Design:
 *  - Every hit carries `title`, `path`, `group`, `icon`, `keywords[]`.
 *  - Scoring is token-based: split the query on whitespace, score each hit by
 *    how many tokens hit (title + keywords + path), boosted when tokens are
 *    contiguous in the title.
 *  - Zero external deps, runs sync on client OR server.
 */
import { PAKISTAN_CITIES, INDIA_CITIES } from "@/lib/gold-data";
import { EARNING_APPS } from "@/lib/earning-apps/apps";

export type SearchGroup = "City" | "Calculator" | "Bill" | "Hub" | "Editorial" | "Developer" | "EarningApp";
export interface SearchHit {
  title: string;
  path: string;
  group: SearchGroup;
  icon: string; // emoji indicator
  keywords: string[];
  /** internal weight boost (default 1) */
  weight?: number;
}

// ---------- Static curated entries ----------
const STATIC: SearchHit[] = [
  // Country hubs
  { title: "Gold Rate — Pakistan (Live)", path: "/pakistan", group: "Hub", icon: "🇵🇰", keywords: ["gold", "rate", "price", "pakistan", "pkr", "tola", "gram"], weight: 1.4 },
  { title: "Gold Rate — India (Live)", path: "/india", group: "Hub", icon: "🇮🇳", keywords: ["gold", "rate", "price", "india", "inr", "tola", "gram", "sona", "bhav"], weight: 1.4 },
  { title: "Gold Rate — UAE / Dubai (Live)", path: "/uae", group: "Hub", icon: "🇦🇪", keywords: ["gold", "rate", "price", "uae", "dubai", "aed", "abu", "dhabi", "sharjah"], weight: 1.4 },
  { title: "Gold Rate — Saudi Arabia (Live)", path: "/ar/gold-rate-saudi-arabia", group: "Hub", icon: "🇸🇦", keywords: ["gold", "rate", "price", "saudi", "arabia", "ksa", "sar", "riyadh", "jeddah"], weight: 1.4 },
  { title: "Silver Rate — Pakistan", path: "/silver-rate-pakistan", group: "Hub", icon: "🥈", keywords: ["silver", "chandi", "rate", "pakistan"], weight: 1.3 },
  { title: "Silver Rate — India", path: "/silver-rate-india", group: "Hub", icon: "🥈", keywords: ["silver", "chandi", "rate", "india"], weight: 1.3 },

  // Calculators
  { title: "Zakat Calculator", path: "/zakat-calculator", group: "Calculator", icon: "🕌", keywords: ["zakat", "calculator", "nisab", "islamic", "charity", "sadaqah"], weight: 1.2 },
  { title: "Gold Purity Converter (Karat / Fineness)", path: "/tools/gold-purity-converter", group: "Calculator", icon: "🧮", keywords: ["purity", "karat", "carat", "fineness", "24k", "22k", "18k", "converter"] },
  { title: "Jewelry Making Estimator (Wastage + Making)", path: "/tools/jewelry-making-estimator", group: "Calculator", icon: "💍", keywords: ["jewelry", "jewellery", "making", "wastage", "charges", "estimator"] },
  { title: "Jewelry Wastage Calculator", path: "/jewelry-wastage-calculator", group: "Calculator", icon: "💍", keywords: ["wastage", "jewellery", "jewelry", "calculator"] },
  { title: "Historical ROI Calculator (Gold Investment)", path: "/tools/historical-roi-calculator", group: "Calculator", icon: "📈", keywords: ["roi", "return", "investment", "historical", "past", "calculator", "profit"] },
  { title: "Gold Investment Calculator", path: "/gold-investment-calculator", group: "Calculator", icon: "📈", keywords: ["gold", "investment", "calculator", "sip"] },
  { title: "Gold Purity Calculator", path: "/gold-purity-calculator", group: "Calculator", icon: "🧮", keywords: ["gold", "purity", "calculator", "hallmark"] },
  { title: "Tools Suite (All Calculators)", path: "/tools", group: "Calculator", icon: "🧰", keywords: ["tools", "calculators", "suite", "all"] },

  // Bill checkers — dedicated /check-*


  // Developer / Editorial
  { title: "Free Gold Rate API (Developers)", path: "/developer/gold-rate-api", group: "Developer", icon: "🔌", keywords: ["api", "developer", "json", "gold", "rate", "free"] },
  { title: "Live Gold Rate API", path: "/live-gold-rate-api", group: "Developer", icon: "🔌", keywords: ["api", "live", "gold", "rate"] },
  { title: "Embed Gold Widgets", path: "/embed-tools", group: "Developer", icon: "🧩", keywords: ["embed", "widget", "iframe", "javascript"] },
  { title: "Blog", path: "/blog", group: "Editorial", icon: "📝", keywords: ["blog", "articles", "posts"] },
  { title: "Learn", path: "/learn", group: "Editorial", icon: "📚", keywords: ["learn", "guide", "tutorial", "explainer"] },
  { title: "All Calculators", path: "/calculators", group: "Editorial", icon: "🗂️", keywords: ["directory", "all", "pages", "index", "calculators"] },
  { title: "About DrHint", path: "/about-us", group: "Editorial", icon: "ℹ️", keywords: ["about", "us", "company"] },
  { title: "Contact", path: "/contact", group: "Editorial", icon: "✉️", keywords: ["contact", "email", "reach"] },
];

function billCheckers(rows: Array<[string, string, string, string[]]>): SearchHit[] {
  return rows.map(([slug, title, path, extra]) => ({
    title,
    path,
    group: "Bill",
    icon: "🧾",
    keywords: ["bill", slug, "check", "duplicate", "online", "wapda", ...extra],
    weight: 1.1,
  }));
}

// ---------- Dynamic city entries ----------
// Cities with dedicated top-level route files:
const DEDICATED_GOLD_CITY = new Set(["karachi", "lahore", "delhi", "mumbai"]);
const DEDICATED_SILVER_CITY = new Set([
  "ahmedabad", "bahawalpur", "bangalore", "chennai", "faisalabad", "gujranwala",
  "hyderabad-india", "hyderabad-pakistan", "islamabad", "kanpur", "karachi",
  "kolkata", "lahore", "multan", "mumbai", "nagpur", "peshawar", "pune",
  "quetta", "rawalpindi", "sargodha", "sialkot", "surat",
]);

function citySilverPath(citySlug: string, country: "pakistan" | "india"): string {
  // Silver files use hyderabad-india / hyderabad-pakistan (not the gold city slug)
  if (citySlug === "hyderabad-pk") return "/silver-rate-hyderabad-pakistan";
  if (citySlug === "hyderabad-in") return "/silver-rate-hyderabad-india";
  if (DEDICATED_SILVER_CITY.has(citySlug)) return `/silver-rate-${citySlug}`;
  return `/city/${country}/${citySlug}`;
}
function cityGoldPath(citySlug: string, country: "pakistan" | "india"): string {
  if (DEDICATED_GOLD_CITY.has(citySlug)) return `/gold-rate-${citySlug}`;
  return `/city/${country}/${citySlug}`;
}

function cityHits(): SearchHit[] {
  const hits: SearchHit[] = [];
  const rows: Array<[string, string, "pakistan" | "india", string[]]> = [
    ...PAKISTAN_CITIES.map((c) => [c.slug, c.name, "pakistan", ["pkr", "tola", "pakistani"]] as [string, string, "pakistan", string[]]),
    ...INDIA_CITIES.map((c) => [c.slug, c.name, "india", ["inr", "gram", "indian", "sona", "chandi", "bhav"]] as [string, string, "india", string[]]),
  ];
  for (const [slug, name, country, extra] of rows) {
    const nameLower = name.toLowerCase();
    hits.push({
      title: `Gold Rate in ${name}`,
      path: cityGoldPath(slug, country),
      group: "City",
      icon: "📍",
      keywords: ["gold", "rate", "price", "today", nameLower, country, ...extra],
    });
    hits.push({
      title: `Silver Rate in ${name}`,
      path: citySilverPath(slug, country),
      group: "City",
      icon: "📍",
      keywords: ["silver", "chandi", "rate", "price", nameLower, country, ...extra],
    });
  }
  // UAE + Saudi popular cities (redirect to hub with anchor)
  for (const c of ["dubai", "abu-dhabi", "sharjah", "ajman"]) {
    hits.push({ title: `Gold Rate in ${cap(c)}`, path: `/uae#${c}`, group: "City", icon: "📍", keywords: ["gold", "rate", "uae", "aed", c] });
  }
  for (const c of ["riyadh", "jeddah", "mecca", "medina", "dammam"]) {
    hits.push({ title: `Gold Rate in ${cap(c)}`, path: `/ar/gold-rate-saudi-arabia#${c}`, group: "City", icon: "📍", keywords: ["gold", "rate", "saudi", "sar", c] });
  }
  return hits;
}
function cap(s: string) { return s.split("-").map((p) => p[0].toUpperCase() + p.slice(1)).join(" "); }

// ---------- Earning Apps ----------
function earningAppHits(): SearchHit[] {
  const hub: SearchHit = {
    title: "Earning Apps Review — Real Or Fake",
    path: "/earning-apps-review",
    group: "EarningApp",
    icon: "💸",
    keywords: ["earning", "app", "apps", "review", "real", "fake", "honest", "verdict", "money", "side", "income", "kamai", "paisa"],
    weight: 1.3,
  };
  const leaves: SearchHit[] = EARNING_APPS.map((a) => ({
    title: `${a.name} — Real Or Fake? Review`,
    path: `/earning-apps-review/${a.slug}`,
    group: "EarningApp",
    icon: a.verdict === "Real" ? "✅" : a.verdict === "Mixed" ? "⚖️" : a.verdict === "Risky" ? "⚠️" : "❌",
    keywords: [
      "earning", "app", "review", "real", "fake", "legit", "scam",
      a.name.toLowerCase(), a.slug, a.verdict.toLowerCase(),
      ...a.category.toLowerCase().split(/\s+/),
    ],
    weight: 1.15,
  }));
  return [hub, ...leaves];
}

// ---------- Assemble ----------
let CACHE: SearchHit[] | null = null;
export function getSearchIndex(): SearchHit[] {
  if (CACHE) return CACHE;
  CACHE = [...STATIC, ...cityHits(), ...earningAppHits()];
  return CACHE;
}

// ---------- Fuzzy scoring ----------
const STOP = new Set(["in", "at", "the", "of", "for", "and", "a", "an", "to", "is", "on", "per", "with", "today", "live", "now"]);
// Common intent normalizations
const ALIAS: Record<string, string> = {
  sona: "gold", sone: "gold", bhav: "gold", bhaav: "gold",
  chandi: "silver", chaandi: "silver",
  kesc: "kelectric", "k-electric": "kelectric",
  wapda: "bill", bijli: "electricity", bijly: "electricity",
  tola: "gold", tolla: "gold",
  emi: "loan", qarz: "loan", karz: "loan",
  nisab: "zakat", zakaat: "zakat", zakah: "zakat",
};

function tokenize(q: string): string[] {
  return q.toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .split(/\s+/)
    .filter((t) => t && !STOP.has(t))
    .map((t) => ALIAS[t] ?? t);
}

export interface ScoredHit extends SearchHit { score: number }

export function searchIndex(query: string, limit = 8): ScoredHit[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) return [];
  const idx = getSearchIndex();
  const scored: ScoredHit[] = [];
  const raw = query.trim().toLowerCase();
  for (const h of idx) {
    const hayTitle = h.title.toLowerCase();
    const hayPath = h.path.toLowerCase();
    const kws = new Set(h.keywords.map((k) => k.toLowerCase()));
    let score = 0;
    let matched = 0;
    for (const t of tokens) {
      let hit = false;
      if (kws.has(t)) { score += 3; hit = true; }
      else if (hayTitle.includes(t)) { score += 2; hit = true; }
      else if (hayPath.includes(t)) { score += 1.2; hit = true; }
      else {
        // partial substring in any keyword
        for (const k of kws) {
          if (k.length >= 4 && (k.includes(t) || t.includes(k))) { score += 1; hit = true; break; }
        }
      }
      if (hit) matched += 1;
    }
    // require at least one token to hit
    if (matched === 0) continue;
    // coverage bonus (all tokens matched)
    if (matched === tokens.length) score += 1.5;
    // contiguous phrase in title
    if (raw.length > 3 && hayTitle.includes(raw)) score += 3;
    // weight boost
    score *= h.weight ?? 1;
    scored.push({ ...h, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

/** Returns a single best hit if score is high enough to warrant auto-redirect on Enter. */
export function bestExactHit(query: string): ScoredHit | null {
  const top = searchIndex(query, 3);
  if (top.length === 0) return null;
  const [a, b] = top;
  if (a.score >= 6 && (!b || a.score - b.score >= 2)) return a;
  return null;
}